import { createTheme } from '@mui/material'
import { alertClasses } from '@mui/material/Alert'
import { buttonClasses } from '@mui/material/Button'

// Stratis UI font
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'

import 'country-flag-icons/react/3x2'

// material-symbols font
import '@fontsource-variable/material-symbols-rounded/index.css'

import type {} from '@mui/x-date-pickers/themeAugmentation'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    displayLarge: React.CSSProperties
    displayMedium: React.CSSProperties
    displaySmall: React.CSSProperties
    body1Medium: React.CSSProperties
    body1SemiBold: React.CSSProperties
    body2Medium: React.CSSProperties
    body2SemiBold: React.CSSProperties
    body3: React.CSSProperties
    body3Medium: React.CSSProperties
    body3SemiBold: React.CSSProperties
    body4: React.CSSProperties
    body4Medium: React.CSSProperties
    body4SemiBold: React.CSSProperties
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    displayLarge?: React.CSSProperties
    displayMedium?: React.CSSProperties
    displaySmall?: React.CSSProperties
    body1Medium?: React.CSSProperties
    body1SemiBold?: React.CSSProperties
    body2Medium?: React.CSSProperties
    body2SemiBold?: React.CSSProperties
    body3?: React.CSSProperties
    body3Medium?: React.CSSProperties
    body3SemiBold?: React.CSSProperties
    body4?: React.CSSProperties
    body4Medium?: React.CSSProperties
    body4SemiBold?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    displayLarge: true
    displayMedium: true
    displaySmall: true
    body1Medium: true
    body1SemiBold: true
    body2Medium: true
    body2SemiBold: true
    body3: true
    body3Medium: true
    body3SemiBold: true
    body4: true
    body4Medium: true
    body4SemiBold: true
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    red: {
      100: string
      200: string
      300: string
      400: string
      500: string
      800: string
      900: string
      1000: string
      1100: string
    }
    blue: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
      1200: string
    }
    green: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
    }
    orange: {
      100: string
      200: string
      300: string
      400: string
      500: string
      800: string
      900: string
      1000: string
      1100: string
      1200: string
    }
    yellow: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      800: string
      1300: string
    }
    purple: {
      700: string
    }
    neutralGrey: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
      1200: string
      1300: string
    }
    coolGrey: {
      100: string
    }
    white: string
    forest: {
      100: string
      200: string
      1100: string
    }
    teal: {
      100: string
      200: string
      800: string
      1000: string
      1100: string
    }
    violet: {
      100: string
      200: string
      900: string
      1000: string
      1100: string
    }
    magenta: {
      100: string
      200: string
      800: string
      1000: string
      1100: string
    }
  }
  interface PaletteOptions {
    red: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
    }
    blue: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
      1200: string
    }
    green: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
    }
    orange: {
      100: string
      200: string
      300: string
      400: string
      500: string
      800: string
      900: string
      1000: string
      1100: string
      1200: string
    }
    yellow: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      800: string
      1300: string
    }
    purple: {
      700: string
    }
    neutralGrey: {
      100: string
      200: string
      300: string
      400: string
      500: string
      600: string
      700: string
      800: string
      900: string
      1000: string
      1100: string
      1200: string
      1300: string
    }
    coolGrey: {
      100: string
    }
    white: string
    forest: {
      100: string
      200: string
      1100: string
    }
    teal: {
      100: string
      200: string
      800: string
      1000: string
      1100: string
    }
    violet: {
      100: string
      200: string
      900: string
      1000: string
      1100: string
    }
    magenta: {
      100: string
      200: string
      800: string
      1000: string
      1100: string
    }
  }
}

const palette = {
  primary: { main: '#FFD800', contrastText: '#212121', light: '#FFE900', dark: '#FFC400' },
  secondary: { main: '#F8F8F8', contrastText: '#212121', light: '#ECECED', dark: '#e6e6e6' },
  info: { main: '#ebf0fb', contrastText: '#003e97', light: '#f0f4fc', dark: '#d9e2f3', icon: '#113B98' },
  warning: { main: '#fff6e7', contrastText: '#9d6508', light: '#fffaf2', dark: '#f9e8c7', icon: '#BA622E' },
  error: { main: '#ffe3e3', contrastText: '#961616', light: '#fff0f0', dark: '#f9cfcf', icon: '#961616' },
  success: { main: '#e1faea', contrastText: '#016626', light: '#f0fcf7', dark: '#c5f3d9', icon: '#016626' },
  ada: { accent: '#0094FF' },

  // TODO: Move 'nuetral' usages to 'grey', and remove 'nuetral'
  neutral: { main: '#ECECED', contrastText: '#4F4D55', light: '#F1F1F1', dark: '#EFF0F2', icon: '#4C4B53' },
  white: '#FFFFFF',
  grey: {
    50: '#F9F9F9',
    60: '#FBFAFC',
    100: '#F2F2F2',
    110: '#F1F1F1',
    120: '#EFF0F2',
    130: '#ECECED',
    200: '#E5E5E5',
    300: '#D9D9D9',
    350: '#C9C9CC',
    400: '#BFBFBF',
    500: '#A6A6A6',
    600: '#8C8C8C',
    700: '#737373',
    800: '#595959',
    810: '#4F4D55',
    900: '#404040',
    1200: '#1D1C20',
    1300: '#0A090B',
  },
  coolGrey: {
    100: '#FAFBFC',
  },
  neutralGrey: {
    100: '#FFFFFF',
    200: '#F8F8F8',
    300: '#F1F1F1',
    400: '#ECECED',
    500: '#E6E6E6',
    600: '#DCDCDE',
    700: '#C9C9CC',
    800: '#ADACB0',
    900: '#6B6A76',
    1000: '#4C4B53',
    1100: '#3C3E44',
    1200: '#1D1C20',
    1300: '#0A090B',
  },
  green: {
    100: '#EBFBF1',
    200: '#E1FAEA',
    300: '#C1F4D4',
    400: '#91DDAD',
    500: '#63C888',
    600: '#3BBA6A',
    700: '#14AD4D',
    800: '#019939',
    900: '#018030',
    1000: '#016626',
    1100: '#004D1D',
  },
  red: {
    100: '#FEEBEB',
    200: '#FFE3E3',
    300: '#FFC9C9',
    400: '#FAA4A4',
    500: '#FF7F7F',
    600: '#F75656',
    700: '#F53535',
    800: '#E12121',
    900: '#BC1C1C',
    1000: '#961616',
    1100: '#711111',
  },
  orange: {
    100: '#FDF4F2',
    200: '#FCEEEB',
    300: '#F9DBD5',
    400: '#F6C7BD',
    500: '#F3B0A1',
    700: '#ED774E',
    800: '#EB6424',
    900: '#CE5820',
    1000: '#B84E1C',
    1100: '#A04418',
    1200: '#823714',
  },
  blue: {
    100: '#FDFDFF',
    200: '#F6F9FC',
    300: '#EBF0FB',
    400: '#D6E0F7',
    500: '#618DF2',
    600: '#94B0EC',
    700: '#1751D0',
    800: '#4272DD',
    900: '#1751D0',
    1000: '#113B98',
    1100: '#0C296A',
    1200: '#081B45',
  },
  yellow: {
    100: '#FFFAF2',
    200: '#FFF8ED',
    300: '#FFF2D8',
    400: '#FFEBC2',
    500: '#FFE3A9',
    600: '#FFDC8C',
    800: '#FFCC26',
    900: '#ECBD23',
    1000: '#D7AC20',
    1300: '#886D14',
  },
  purple: {
    700: '#8136DC',
  },
  forest: {
    100: '#F5FBF1',
    200: '#E9F3E3',
    1100: '#345123',
  },
  teal: {
    100: '#F3FAFC',
    200: '#DFF1F7',
    800: '#007092',
    1000: '#004B61',
    1100: '#003849',
  },
  violet: {
    100: '#F1E9FB',
    200: '#EADEF9',
    800: '#6E23C9',
    900: '#5C1DA7',
    1000: '#491786',
    1100: '#371264',
  },
  magenta: {
    100: '#F9E9F3',
    200: '#F5DDED',
    800: '#B01F80',
    1000: '#751556',
    1100: '#581040',
  },
}

const typography = {
  fontFamily: 'Inter, sans-serif !important', // important to override designer.css
  fontSize: 14,
  displayLarge: {
    fontSize: 72,
    lineHeight: '80px',
    fontWeight: 700,
    letterSpacing: 0,
  },
  displayMedium: {
    fontSize: 64,
    lineHeight: '72px',
    fontWeight: 700,
    letterSpacing: 0,
  },
  displaySmall: {
    fontSize: 56,
    lineHeight: '64px',
    fontWeight: 700,
    letterSpacing: 0,
  },
  h1: {
    fontSize: 48,
    lineHeight: '56px',
    fontWeight: 600,
    letterSpacing: '-0.48px',
  },
  h2: {
    fontSize: 40,
    lineHeight: '48px',
    fontWeight: 600,
    letterSpacing: '-0.4px',
  },
  h3: {
    fontSize: 32,
    lineHeight: '40px',
    fontWeight: 600,
    letterSpacing: '-0.32px',
  },
  h4: {
    fontSize: 24,
    lineHeight: '32px',
    fontWeight: 600,
    letterSpacing: '-0.24px',
  },
  h5: {
    fontSize: 20,
    lineHeight: '28px',
    fontWeight: 600,
    letterSpacing: '-0.2px',
  },
  h6: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
    letterSpacing: '-0.16px',
  },
  body1: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 400,
    letterSpacing: 0,
  },
  body1Medium: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 500,
    letterSpacing: 0,
  },
  body1SemiBold: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
    letterSpacing: 0,
  },
  body2: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    letterSpacing: 0,
  },
  body2Medium: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 500,
    letterSpacing: 0,
  },
  body2SemiBold: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
    letterSpacing: 0,
  },
  body3: {
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: 0,
  },
  body3Medium: {
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    letterSpacing: 0,
  },
  body3SemiBold: {
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    letterSpacing: 0,
  },
  body4: {
    fontSize: 10,
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: 0,
  },
  body4Medium: {
    fontSize: 10,
    lineHeight: '16px',
    fontWeight: 500,
    letterSpacing: 0,
  },
  body4SemiBold: {
    fontSize: 10,
    lineHeight: '16px',
    fontWeight: 600,
    letterSpacing: 0,
  },
  caption: {
    fontSize: 12,
    fontStyle: 'italic',
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: '0.12px',
  },
  overline: {
    fontSize: 10,
    lineHeight: '12px',
    fontWeight: 600,
    letterSpacing: '0.2px',
  },
}

const BOX_SHADOW_CONFIGS = {
  soft: '0px 1.5px 4px -1px rgba(0, 0, 0, 0.07)',
  menu: '0px 6px 16px 0px rgba(10, 9, 11, 0.08)',
}

const breakpoints = {
  values: {
    xs: 0,
    sm: 680,
    md: 850,
    lg: 1000,
    xl: 1280,
  },
}

const STANDARD_BORDER = '1px solid ' + palette.grey[110]
const STANDARD_OUTLINE = {
  borderRadius: 8,
  border: STANDARD_BORDER,
  boxShadow: '0px 1.5px 4px -1px rgba(10, 9, 11, 0.07)',
}

const FILE_INPUT_DROPZONE = {
  border: '1px solid ' + palette.grey[500],
  borderStyle: 'dashed',
  borderRadius: 6,
  backgroundColor: palette.grey[50],
}

export const border = {
  radius: {
    medium: '4px',
  },
  width: {
    thin: '1px',
  },
}

export const BaseTheme = createTheme({
  mixins: {
    borderStandard: {
      border: STANDARD_BORDER,
    },
    outlineStandard: {
      ...STANDARD_OUTLINE,
    },
    fileInputDropzone: {
      ...FILE_INPUT_DROPZONE,
    },
  },
  typography,
  palette,
  breakpoints,
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&::before': {
            display: 'none',
          },
          padding: 0,
          boxShadow: '0px 2px 16px 0px rgba(17, 17, 28, 0.08)',
          border: `1px solid ${palette.neutralGrey[300]}`,
          borderRadius: '8px',
          [createTheme().breakpoints.down(393)]: {
            borderRadius: '4px',
          },
          color: palette.neutralGrey[1300],
          '& .OSUI-material-symbols': {
            color: palette.neutralGrey[1300],
          },
          '& .OSUI-Collapse-root': {
            borderTop: `1px solid ${palette.neutralGrey[300]}`,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0px 16px',
          minHeight: 72,
          height: 72,
          [createTheme().breakpoints.between(393, 'xl')]: {
            minHeight: '64px !important',
          },
          [createTheme().breakpoints.down(393)]: {
            padding: '12px',
            minHeight: '44px !important',
          },
        },
        content: {
          fontFamily: typography.fontFamily,
          alignItems: 'center',
          gap: '4px !important',
          ...typography.body2Medium,
          [createTheme().breakpoints.down(393)]: {
            ...typography.body3Medium,
          },
        },
        expandIconWrapper: {
          width: 24,
          height: 24,
          '& .OSUI-material-symbols': {
            fontSize: '24px !important',
          },
          [createTheme().breakpoints.down(393)]: {
            width: 20,
            height: 20,
            '& .OSUI-material-symbols': {
              fontSize: '20px !important',
            },
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '32px',
          [createTheme().breakpoints.down(393)]: {
            padding: '16px',
          },
        },
      },
    },
    MuiBadge: {
      defaultProps: {
        color: 'primary',
      },
      variants: [
        {
          props: { variant: 'dot' },
          style: {
            width: 8,
            height: 8,
            borderRadius: '50%',
          },
        },
        {
          props: { variant: 'dot', color: 'success', emphasis: 'intense' },
          style: {
            backgroundColor: palette.green[1000],
          },
        },
        {
          props: { variant: 'dot', color: 'error', emphasis: 'intense' },
          style: {
            backgroundColor: palette.red[800],
          },
        },
        {
          props: { variant: 'dot', color: 'warning', emphasis: 'intense' },
          style: {
            backgroundColor: palette.orange[1000],
          },
        },
        {
          props: { variant: 'dot', color: 'info', emphasis: 'intense' },
          style: {
            backgroundColor: palette.blue[900],
          },
        },
        {
          props: { variant: 'dot', color: 'primary', emphasis: 'intense' },
          style: {
            backgroundColor: palette.neutralGrey[1300],
          },
        },
        {
          props: { variant: 'standard', size: 'small' },
          style: {
            padding: '0px 4px 0px 4px',
            minHeight: 20,
          },
        },
        {
          props: { variant: 'standard', size: 'medium' },
          style: {
            padding: '0px 8px 0px 8px',
            minHeight: 24,
          },
        },
        {
          props: { variant: 'standard', size: 'large' },
          style: {
            padding: '0px 12px 0px 12px',
            minHeight: 28,
          },
        },
        {
          props: { variant: 'standard', color: 'success', emphasis: 'subtle' },
          style: {
            backgroundColor: palette.green[200],
            color: palette.green[1000],
          },
        },
        {
          props: { variant: 'standard', color: 'error', emphasis: 'subtle' },
          style: {
            backgroundColor: palette.red[200],
            color: palette.red[1000],
          },
        },
        {
          props: { variant: 'standard', color: 'warning', emphasis: 'subtle' },
          style: {
            backgroundColor: palette.orange[100],
            color: palette.orange[1000],
          },
        },
        {
          props: { variant: 'standard', color: 'info', emphasis: 'subtle' },
          style: {
            backgroundColor: palette.blue[200],
            color: palette.blue[1000],
          },
        },
        {
          props: { variant: 'standard', color: 'primary', emphasis: 'subtle' },
          style: {
            backgroundColor: palette.neutralGrey[300],
            color: palette.neutralGrey[1100],
          },
        },

        {
          props: { variant: 'standard', color: 'success', emphasis: 'intense' },
          style: {
            backgroundColor: palette.green[900],
          },
        },
        {
          props: { variant: 'standard', color: 'error', emphasis: 'intense' },
          style: {
            backgroundColor: palette.red[800],
          },
        },
        {
          props: { variant: 'standard', color: 'warning', emphasis: 'intense' },
          style: {
            backgroundColor: palette.orange[1000],
          },
        },
        {
          props: { variant: 'standard', color: 'info', emphasis: 'intense' },
          style: {
            backgroundColor: palette.blue[900],
          },
        },
        {
          props: { variant: 'standard', color: 'primary', emphasis: 'intense' },
          style: {
            backgroundColor: palette.neutralGrey[1300],
          },
        },
      ],
      styleOverrides: {
        root: {
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          gap: 4,
          color: palette.white,
          ...typography.body3Medium,
          '& .OSUI-material-symbols': {
            fontSize: '16px !important',
          },
        },
        dot: {
          display: 'none',
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { size: 'xSmall' },
          style: {
            '& .OSUI-Button-startIcon > *, & .OSUI-Button-endIcon > *': {
              width: 20,
              height: 20,
            },
            '& :is(.OSUI-Button-startIcon, .OSUI-Button-endIcon) > .OSUI-material-symbols': {
              fontSize: '20px !important',
            },
            minHeight: 32,
            paddingLeft: 16,
            paddingRight: 16,
            gap: 4,
            ...typography.body3Medium,
          },
        },
        {
          props: { size: 'xXSmall' },
          style: {
            '& .OSUI-Button-startIcon > *, & .OSUI-Button-endIcon > *': {
              width: 20,
              height: 20,
            },
            '& :is(.OSUI-Button-startIcon, .OSUI-Button-endIcon) > .OSUI-material-symbols': {
              fontSize: '20px !important',
            },
            minHeight: 28,
            paddingLeft: 8,
            paddingRight: 8,
            gap: 4,
            ...typography.body3Medium,
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          minWidth: 0,
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 0,
          paddingBottom: 0,
          gap: 8,
          borderRadius: 4,
          backgroundColor: palette.neutralGrey[100],
          '& .OSUI-Button-startIcon, & .OSUI-Button-endIcon': {
            margin: 0,
            justifyContent: 'center',
            alignItems: 'center',
          },
          // https://mui.com/material-ui/migration/migrating-from-deprecated-apis/#button
          [`&.${buttonClasses.contained}.${buttonClasses.colorPrimary}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1300],
            },
            backgroundColor: palette.yellow[800],
            color: palette.neutralGrey[1300],
            '&:hover': {
              backgroundColor: palette.yellow[600],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.yellow[300]}`,
              backgroundColor: palette.yellow[600],
            },
            '&:disabled': {
              backgroundColor: palette.yellow[300],
              color: palette.neutralGrey[700],
              '& .MuiSvgIcon-root': {
                color: `${palette.neutralGrey[700]}`,
              },
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorPrimary}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            color: palette.neutralGrey[1000],
            border: `1.5px solid ${palette.yellow[800]}`,
            backgroundColor: palette.neutralGrey[100],
            '&:hover': {
              border: `1.5px solid ${palette.yellow[500]}`,
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.yellow[300]}`,
            },
            '&:disabled': {
              color: palette.neutralGrey[700],
              border: `1.5px solid ${palette.yellow[500]}`,
              '& .MuiSvgIcon-root': {
                color: `${palette.neutralGrey[700]}`,
              },
            },
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorPrimary}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.yellow[900],
            },
            backgroundColor: 'transparent',
            color: palette.yellow[900],
            border: 'none',
            '&:hover': {
              color: palette.yellow[700],
            },
            '&:focus': {
              color: palette.yellow[900],
              boxShadow: `0px 0px 0px 4px ${palette.yellow[300]}`,
            },
            '&:disabled': {
              color: palette.yellow[500],
              '& .MuiSvgIcon-root': {
                color: `${palette.neutralGrey[500]}`,
              },
            },
          },
          [`&.${buttonClasses.contained}.${buttonClasses.colorSecondary}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.blue[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.blue[1000],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
              backgroundColor: palette.blue[1000],
            },
            '&:disabled': {
              backgroundColor: palette.blue[400],
              color: palette.neutralGrey[200],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[200],
              },
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorSecondary}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            color: palette.neutralGrey[1000],
            border: `1.5px solid ${palette.blue[800]}`,
            '&:hover': {
              border: `1.5px solid ${palette.blue[1000]}`,
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
            },
            '&:disabled': {
              border: `1.5px solid ${palette.blue[400]}`,
              color: palette.neutralGrey[800],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[800],
              },
            },
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorSecondary}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.blue[900],
            },
            color: palette.blue[900],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.blue[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
            },
            '&:disabled': {
              color: palette.blue[500],
              '& .MuiSvgIcon-root': {
                color: palette.blue[500],
              },
            },
          },
          [`&.${buttonClasses.contained}.${buttonClasses.colorInfo}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            backgroundColor: palette.neutralGrey[200],
            color: palette.neutralGrey[1000],
            '&:hover': {
              backgroundColor: palette.neutralGrey[300],
              color: palette.neutralGrey[1300],
            },
            '&:focus': {
              backgroundColor: palette.neutralGrey[300],
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
            },
            '&:disabled': {
              backgroundColor: palette.neutralGrey[200],
              color: palette.neutralGrey[500],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[500],
              },
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorInfo}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            border: `1px solid ${palette.neutralGrey[500]}`,
            color: palette.neutralGrey[1000],
            '&:hover': {
              color: palette.blue[1000],
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
              color: palette.blue[1000],
            },
            '&:disabled': {
              color: palette.neutralGrey[500],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[500],
              },
              border: `1px solid ${palette.neutralGrey[200]}`,
            },
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorInfo}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            backgroundColor: 'transparent',
            color: palette.neutralGrey[1000],
            '&:hover': {
              color: palette.neutralGrey[1300],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
              color: palette.neutralGrey[1300],
            },
            '&:disabled': {
              color: palette.neutralGrey[500],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[500],
              },
            },
          },
          [`&.${buttonClasses.contained}.${buttonClasses.colorSuccess}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.green[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.green[1000],
              color: palette.neutralGrey[100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.green[200]}`,
              backgroundColor: palette.green[1000],
            },
            '&:disabled': {
              backgroundColor: palette.green[200],
              color: palette.neutralGrey[100],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[100],
              },
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorSuccess}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            color: palette.neutralGrey[1000],
            border: `1.5px solid ${palette.green[800]}`,
            '&:hover': {
              border: `1.5px solid ${palette.green[1000]}`,
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.green[400]}`,
            },
            '&:disabled': {
              border: `1.5px solid ${palette.green[400]}`,
              color: palette.neutralGrey[800],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[800],
              },
            },
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorSuccess}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.green[900],
            },
            color: palette.green[900],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.green[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.green[400]}`,
            },
            '&:disabled': {
              color: palette.green[500],
              '& .MuiSvgIcon-root': {
                color: palette.green[500],
              },
            },
          },
          [`&.${buttonClasses.contained}.${buttonClasses.colorWarning}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.orange[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.orange[1000],
            },
            '&:focus': {
              backgroundColor: palette.orange[1000],
              boxShadow: `0px 0px 0px 4px ${palette.orange[200]}`,
            },
            '&:disabled': {
              backgroundColor: palette.orange[300],
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorWarning}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.orange[1000],
            },
            color: palette.orange[1000],
            border: `1.5px solid ${palette.orange[800]}`,
            '&:hover': {
              border: `1.5px solid ${palette.orange[1000]}`,
              color: palette.orange[1100],
              backgroundColor: palette.neutralGrey[100],
            },
            '&:focus': {
              border: 'none',
              color: palette.orange[1200],
              boxShadow: `0px 0px 0px 4px ${palette.orange[200]}`,
            },
            '&:disabled': {
              color: palette.orange[300],
              border: `1.5px solid ${palette.orange[200]}`,
            },
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorWarning}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.orange[1000],
            },
            color: palette.orange[1000],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.orange[1100],
            },
            '&:focus': {
              color: palette.orange[1200],
              boxShadow: `0px 0px 0px 4px ${palette.orange[200]}`,
            },
            '&:disabled': {
              color: palette.orange[300],
            },
          },
          [`&.${buttonClasses.contained}.${buttonClasses.colorError}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.red[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.red[1000],
              color: palette.neutralGrey[100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.red[200]}`,
              backgroundColor: palette.red[1000],
            },
            '&:disabled': {
              backgroundColor: palette.red[300],
              color: palette.neutralGrey[100],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[100],
              },
            },
          },
          [`&.${buttonClasses.outlined}.${buttonClasses.colorError}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.red[1000],
            },
            border: `1.5px solid ${palette.red[800]}`,
            color: palette.red[1000],
            '&:hover': {
              border: `1.5px solid ${palette.red[1000]}`,
              color: palette.red[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.red[200]}`,
              color: palette.red[1100],
            },
            '&:disabled': {
              border: `1.5px solid ${palette.red[200]}`,
              color: palette.red[300],
              '& .MuiSvgIcon-root': {
                color: palette.red[300],
              },
            },
          },
          [`&.${buttonClasses.text}.${buttonClasses.colorError}`]: {
            '& .MuiSvgIcon-root': {
              color: palette.red[1000],
            },
            color: palette.red[1000],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.red[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.red[200]}`,
              color: palette.red[1000],
            },
            '&:disabled': {
              color: palette.red[300],
              '& .MuiSvgIcon-root': {
                color: palette.red[300],
              },
            },
          },
        },
        sizeLarge: {
          '& .OSUI-Button-startIcon > *, & .OSUI-Button-endIcon > *': {
            width: 24,
            height: 24,
          },
          '& :is(.OSUI-Button-startIcon, .OSUI-Button-endIcon) > .OSUI-material-symbols': {
            fontSize: '24px !important',
          },
          minHeight: 48,
          ...typography.body1Medium,
        },
        sizeMedium: {
          '& .OSUI-Button-startIcon > *, & .OSUI-Button-endIcon > *': {
            width: 24,
            height: 24,
          },
          '& :is(.OSUI-Button-startIcon, .OSUI-Button-endIcon) > .OSUI-material-symbols': {
            fontSize: '24px !important',
          },
          minHeight: 40,
          ...typography.body2Medium,
        },
        sizeSmall: {
          '& .OSUI-Button-startIcon > *, & .OSUI-Button-endIcon > *': {
            width: 20,
            height: 20,
          },
          '& :is(.OSUI-Button-startIcon, .OSUI-Button-endIcon) > .OSUI-material-symbols': {
            fontSize: '20px !important',
          },
          minHeight: 36,
          ...typography.body2Medium,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'filled', color: 'primary', size: 'small' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${palette.blue[500]} !important`,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary', size: 'small' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${palette.blue[400]} !important`,
            },
          },
        },
        {
          props: { variant: 'filled', color: 'primary', size: 'medium' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[500]} !important`,
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary', size: 'medium' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]} !important`,
            },
          },
        },
        {
          props: { color: 'error', size: 'small' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${palette.orange[400]} !important`,
            },
          },
        },
        {
          props: { color: 'error', size: 'medium' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.orange[400]} !important`,
            },
          },
        },
        {
          props: { color: 'warning', size: 'small' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${palette.orange[400]} !important`,
            },
          },
        },
        {
          props: { color: 'warning', size: 'medium' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.orange[400]} !important`,
            },
          },
        },
        {
          props: { color: 'success', size: 'small' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${palette.blue[400]} !important`,
            },
          },
        },
        {
          props: { color: 'success', size: 'medium' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]} !important`,
            },
          },
        },
        {
          props: { color: 'default', size: 'small' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 2px ${palette.blue[400]} !important`,
            },
          },
        },
        {
          props: { color: 'default', size: 'medium' },
          style: {
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]} !important`,
            },
          },
        },
        {
          props: { variant: 'filled', color: 'primary' },
          style: {
            backgroundColor: `${palette.blue[800]} !important`,
            '&:hover': {
              backgroundColor: `${palette.blue[900]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.blue[1000]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.blue[400],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            backgroundColor: `${palette.blue[300]} !important`,
            color: palette.blue[1000],
            '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
              color: `${palette.blue[1000]} !important`,
            },
            '& .OSUI-material-symbols': {
              color: palette.blue[1000],
            },
            '&:hover': {
              backgroundColor: `${palette.blue[400]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.blue[600]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.blue[300],
              color: palette.blue[600],
            },
          },
        },
        {
          props: { variant: 'filled', color: 'error' },
          style: {
            backgroundColor: `${palette.red[800]} !important`,
            '&:hover': {
              backgroundColor: `${palette.red[900]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.red[1000]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.red[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            backgroundColor: `${palette.red[300]} !important`,
            color: palette.red[1000],
            '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
              color: `${palette.red[1000]} !important`,
            },
            '& .OSUI-material-symbols': {
              color: palette.red[1000],
            },
            '&:hover': {
              backgroundColor: `${palette.red[400]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.red[500]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.red[300],
              color: palette.red[400],
            },
          },
        },
        {
          props: { variant: 'filled', color: 'warning' },
          style: {
            backgroundColor: `${palette.orange[800]} !important`,
            '&:hover': {
              backgroundColor: `${palette.orange[900]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.orange[1000]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.orange[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            backgroundColor: `${palette.orange[300]} !important`,
            color: palette.orange[1000],
            '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
              color: `${palette.orange[1000]} !important`,
            },
            '& .OSUI-material-symbols': {
              color: palette.orange[1000],
            },
            '&:hover': {
              backgroundColor: `${palette.orange[400]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.orange[500]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.orange[300],
              color: palette.orange[500],
            },
          },
        },
        {
          props: { variant: 'filled', color: 'success' },
          style: {
            backgroundColor: `${palette.green[800]} !important`,
            '&:hover': {
              backgroundColor: `${palette.green[900]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.green[1000]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.green[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            backgroundColor: `${palette.green[300]} !important`,
            color: palette.green[1000],
            '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
              color: `${palette.green[1000]} !important`,
            },
            '& .OSUI-material-symbols': {
              color: palette.green[1000],
            },
            '&:hover': {
              backgroundColor: `${palette.green[400]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.green[500]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.green[300],
              color: palette.green[400],
            },
          },
        },
        {
          props: { variant: 'filled', color: 'default' },
          style: {
            backgroundColor: `${palette.neutralGrey[900]} !important`,
            '&:hover': {
              backgroundColor: `${palette.neutralGrey[1100]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.neutralGrey[1200]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.neutralGrey[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'default' },
          style: {
            backgroundColor: `${palette.neutralGrey[300]} !important`,
            color: palette.neutralGrey[1100],
            '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
              color: `${palette.neutralGrey[1100]} !important`,
            },
            '& .OSUI-material-symbols': {
              color: palette.neutralGrey[1100],
            },
            '&:hover': {
              backgroundColor: `${palette.neutralGrey[500]} !important`,
            },
            '&:focus': {
              backgroundColor: `${palette.neutralGrey[800]} !important`,
            },
            '&:disabled': {
              backgroundColor: palette.neutralGrey[300],
              color: palette.neutralGrey[600],
            },
          },
        },
      ],
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 'auto',
          border: 'none',
          boxShadow: 'none !important',
          gap: 4,
          '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
            width: 24,
            height: 24,
            margin: 0,
            padding: 0,
          },
          '& .OSUI-Chip-label': {
            padding: 0,
          },
          '& .OSUI-material-symbols': {
            fontSize: '24px !important',
          },
        },
        sizeMedium: {
          padding: 4,
          minHeight: 32,
          ...typography.body2,
        },
        sizeSmall: {
          padding: '3px 4px',
          minHeight: 24,
          ...typography.body3,
        },
        avatarMedium: {
          width: 24,
          height: 24,
        },
        avatarSmall: {
          width: 18,
          height: 18,
        },
        filled: {
          color: palette.neutralGrey[100],
          '& .OSUI-Chip-deleteIcon, & .OSUI-Chip-icon': {
            color: palette.neutralGrey[100],
          },
          '& .OSUI-material-symbols': {
            color: palette.neutralGrey[100],
          },
        },
      },
    },
    MuiCheckbox: {
      variants: [
        {
          props: { color: 'secondary' },
          style: {
            color: palette.secondary.dark + ' !important',
          },
        },
        {
          props: { color: 'info' },
          style: {
            color: palette.info.contrastText + ' !important',
          },
        },
        {
          props: { color: 'success' },
          style: {
            color: palette.success.contrastText + ' !important',
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: palette.warning.contrastText + ' !important',
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: palette.error.contrastText + ' !important',
          },
        },
      ],
    },
    MuiRadio: {
      variants: [
        {
          props: { color: 'secondary' },
          style: {
            color: palette.secondary.dark + ' !important',
          },
        },
        {
          props: { color: 'info' },
          style: {
            color: palette.info.contrastText + ' !important',
          },
        },
        {
          props: { color: 'success' },
          style: {
            color: palette.success.contrastText + ' !important',
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: palette.warning.contrastText + ' !important',
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: palette.error.contrastText + ' !important',
          },
        },
      ],
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          marginLeft: '8px',
          marginRight: '8px',
          width: 36,
          height: 20,
          padding: 0,
          overflow: 'visible',
          '&:focus-within': {
            boxShadow: `0px 0px 0px 4px ${palette.blue[400]} !important`,
            backgroundColor: `${palette.neutralGrey[700]} !important`,
          },
        },
        sizeSmall: {
          height: 16,
          width: 28,
          borderRadius: 8,
          '.OSUI-Switch-switchBase': {
            padding: 0,
            margin: '2px 2px',
            '&.Mui-checked': {
              transform: 'translateX(12px) translateY(0px)',
            },
          },
          '.OSUI-Switch-thumb': {
            width: 12,
            height: 12,
          },
        },
        sizeMedium: {
          height: 20,
          width: 36,
          borderRadius: 10,
          '.OSUI-Switch-input': {
            height: 20 + '!important',
            width: 36 + '!important',
          },
          '.OSUI-Switch-switchBase': {
            padding: 0,
            margin: '2px 2px',
            '&.Mui-checked': {
              transform: 'translateX(16px) translateY(0px)',
            },
          },
          '.OSUI-Switch-thumb': {
            width: 16,
            height: 16,
          },
        },
        switchBase: {
          '&.Mui-disabled': {
            '& .OSUI-Switch-thumb': {
              color: `${palette.neutralGrey[300]} !important`,
            },
            '&.Mui-checked .OSUI-Switch-thumb': {
              color: `${palette.neutralGrey[200]} !important`,
            },
          },
        },
        thumb: {
          boxSizing: 'border-box',
          color: palette.white,
          width: 16,
          height: 16,
          boxShadow: 'none',
        },
        track: {
          opacity: 1 + '!important',
          backgroundColor: `${palette.neutralGrey[600]} !important`,
          borderRadius: 10,
          '.OSUI-Switch-switchBase:hover + &': {
            backgroundColor: `${palette.neutralGrey[700]} !important`,
          },
          '.Mui-disabled + &': {
            backgroundColor: `${palette.neutralGrey[500]} !important`,
            '&:hover': {
              backgroundColor: `${palette.neutralGrey[500]} !important`,
            },
          },
          '.Mui-checked + &': {
            backgroundColor: `${palette.blue[800]} !important`,
          },
          '.Mui-checked:hover + &': {
            backgroundColor: `${palette.blue[1000]} !important`,
          },
          '.Mui-checked.Mui-disabled + &': {
            backgroundColor: `${palette.blue[400]} !important`,
          },
        },
      },
    },
    MuiAlert: {
      variants: [
        {
          props: { severity: 'neutral', variant: 'standard' }, // https://mui.com/material-ui/customization/theme-components/#theme-style-overrides
          style: {
            backgroundColor: palette.neutralGrey[200],
            border: '1px solid ' + palette.neutralGrey[400],
            color: palette.neutralGrey[1000],
            fill: palette.neutralGrey[1000],
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '8px 12px',
          [`&.${alertClasses.filled}.${alertClasses.colorInfo}`]: {
            // https://mui.com/material-ui/migration/migrating-from-deprecated-apis/#alert
            backgroundColor: palette.blue[800],
            color: palette.white,
            fill: palette.white,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorWarning}`]: {
            backgroundColor: palette.orange[800],
            color: palette.white,
            fill: palette.white,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorSuccess}`]: {
            backgroundColor: palette.green[800],
            color: palette.white,
            fill: palette.white,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorError}`]: {
            backgroundColor: palette.red[800],
            color: palette.white,
            fill: palette.white,
          },
          [`&.${alertClasses.standard}.${alertClasses.colorInfo}`]: {
            backgroundColor: palette.blue[100],
            border: '1px solid ' + palette.blue[400],
            color: palette.blue[1000],
            fill: palette.blue[1000],
          },
          [`&.${alertClasses.standard}.${alertClasses.colorWarning}`]: {
            backgroundColor: palette.orange[100],
            border: '1px solid ' + palette.orange[400],
            color: palette.orange[1000],
            fill: palette.orange[1000],
          },
          [`&.${alertClasses.standard}.${alertClasses.colorSuccess}`]: {
            backgroundColor: palette.green[100],
            border: '1px solid ' + palette.green[400],
            color: palette.green[1000],
            fill: palette.green[1000],
          },
          [`&.${alertClasses.standard}.${alertClasses.colorError}`]: {
            backgroundColor: palette.red[100],
            border: '1px solid ' + palette.red[400],
            color: palette.red[1000],
            fill: palette.red[1000],
          },
        },
        message: {
          width: '100%',
          padding: 0,
          overflow: 'visible',
          ...typography.body3,
        },
        icon: {
          display: 'flex',
          alignItems: 'center',
          marginRight: 10,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          ...typography.body2Medium,
          margin: 0,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .OSUI-TextField-root': {
            gap: 4,
          },
          '& .OSUI-FormLabel-root': {
            maxWidth: '100%',
            marginBottom: 0,
          },
          '& .OSUI-FormHelperText-root': {
            marginTop: 0,
            padding: '2px 0',
          },
          '& .OSUI-InputBase-input': {
            height: 36,
            boxSizing: 'border-box',
            padding: '0px !important',
            ...typography.body2,
            color: palette.neutralGrey[1300],
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
            },
          },
          '& .OSUI-InputBase-root': {
            padding: '4px 60px 4px 12px !important',
            height: '100%',
            margin: 0,
          },
          '& .OSUI-OutlinedInput-notchedOutline': {
            borderRadius: 4,
            boxShadow: 'none',
            border: `1px solid ${palette.neutralGrey[500]}`,
            '&:focus-visible': {
              border: `1.5px solid ${palette.blue[800]} !important`,
            },
            '&:hover': {
              border: 'none !important',
            },
          },
          '& .OSUI-OutlinedInput-input.Mui-disabled': {
            '-webkit-text-fill-color': palette.neutralGrey[700],
          },
          '&.Mui-disabled': {
            // To change the color of the icon
            '-webkit-text-fill-color': palette.neutralGrey[800],
          },
          '&.Mui-disabled .OSUI-OutlinedInput-notchedOutline': {
            border: `1px solid ${palette.neutralGrey[500]} !important`,
            backgroundColor: 'transparent',
            color: `${palette.neutralGrey[700]} !important`,
            '& .OSUI-material-symbols': {
              color: `${palette.neutralGrey[800]} !important`,
            },
          },
        },
        endAdornment: {
          display: 'flex',
          gap: 4,
        },
        popupIndicator: {
          width: 20,
          height: 20,
          padding: 0,
          margin: 0,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '&.Mui-disabled': {
            '& .OSUI-material-symbols': {
              color: palette.neutralGrey[800],
            },
          },
          '& .OSUI-material-symbols': {
            color: palette.neutralGrey[900],
          },
        },
        clearIndicator: {
          width: 20,
          height: 20,
          padding: 0,
          margin: 0,
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
          '& .OSUI-material-symbols': {
            color: palette.neutralGrey[900],
          },
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: `${palette.blue[100]} !important`,
            color: palette.blue[1000],
            '&:hover': {
              backgroundColor: palette.blue[200],
            },
            '&.Mui-focused': {
              backgroundColor: palette.blue[200],
            },
          },
          '&.Mui-disabled': {
            color: palette.neutralGrey[500],
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          // Adding rounded styles for old IconButton
          props: { shape: 'rounded' },
          style: {
            borderRadius: '50%',
          },
        },
        {
          props: { size: 'xSmall' },
          style: {
            '& .MuiSvgIcon-root': {
              height: 20,
              width: 20,
            },
            '& .OSUI-material-symbols': {
              fontSize: '20px !important',
            },
            height: 32,
            width: 32,
          },
        },
        {
          props: { size: 'xXSmall' },
          style: {
            '& .MuiSvgIcon-root': {
              height: 20,
              width: 20,
            },
            '& .OSUI-material-symbols': {
              fontSize: '20px !important',
            },
            height: 28,
            width: 28,
          },
        },
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1300],
            },
            backgroundColor: palette.yellow[800],
            color: palette.neutralGrey[1300],
            '&:hover': {
              backgroundColor: palette.yellow[600],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.yellow[300]}`,
              backgroundColor: palette.yellow[600],
            },
            '&:disabled': {
              backgroundColor: palette.yellow[300],
              color: palette.neutralGrey[700],
              '& .MuiSvgIcon-root': {
                color: `${palette.neutralGrey[700]}`,
              },
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            color: palette.neutralGrey[1000],
            border: `1.5px solid ${palette.yellow[800]}`,
            backgroundColor: palette.neutralGrey[100],
            '&:hover': {
              border: `1.5px solid ${palette.yellow[500]}`,
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.yellow[300]}`,
            },
            '&:disabled': {
              color: palette.neutralGrey[700],
              border: `1.5px solid ${palette.yellow[500]}`,
              '& .MuiSvgIcon-root': {
                color: `${palette.neutralGrey[700]}`,
              },
            },
          },
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.yellow[900],
            },
            backgroundColor: 'transparent',
            color: palette.yellow[900],
            boder: 'none',
            '&:hover': {
              color: palette.yellow[700],
            },
            '&:focus': {
              color: palette.yellow[900],
              boxShadow: `0px 0px 0px 4px ${palette.yellow[300]}`,
            },
            '&:disabled': {
              color: palette.yellow[500],
              '& .MuiSvgIcon-root': {
                color: `${palette.neutralGrey[500]}`,
              },
            },
          },
        },
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.blue[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.blue[1000],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
              backgroundColor: palette.blue[1000],
            },
            '&:disabled': {
              backgroundColor: palette.blue[400],
              color: palette.neutralGrey[200],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[200],
              },
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            color: palette.neutralGrey[1000],
            border: `1.5px solid ${palette.blue[800]}`,
            '&:hover': {
              border: `1.5px solid ${palette.blue[1000]}`,
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
            },
            '&:disabled': {
              border: `1.5px solid ${palette.blue[400]}`,
              color: palette.neutralGrey[800],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[800],
              },
            },
          },
        },
        {
          props: { variant: 'text', color: 'secondary' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.blue[900],
            },
            color: palette.blue[900],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.blue[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
            },
            '&:disabled': {
              color: palette.blue[500],
              '& .MuiSvgIcon-root': {
                color: palette.blue[500],
              },
            },
          },
        },
        {
          props: { variant: 'contained', color: 'info' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            backgroundColor: palette.neutralGrey[200],
            color: palette.neutralGrey[1000],
            '&:hover': {
              backgroundColor: palette.neutralGrey[300],
              color: palette.neutralGrey[1300],
            },
            '&:focus': {
              backgroundColor: palette.neutralGrey[300],
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
            },
            '&:disabled': {
              backgroundColor: palette.neutralGrey[200],
              color: palette.neutralGrey[500],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[500],
              },
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'info' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            border: `1px solid ${palette.neutralGrey[500]}`,
            backgroundColor: palette.neutralGrey[100],
            color: palette.neutralGrey[1000],
            '&:hover': {
              color: palette.blue[1000],
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
              color: palette.blue[1000],
            },
            '&:disabled': {
              color: palette.neutralGrey[500],
              backgroundColor: palette.neutralGrey[100],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[500],
              },
              border: `1px solid ${palette.neutralGrey[200]}`,
            },
          },
        },
        {
          props: { variant: 'text', color: 'info' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            backgroundColor: 'transparent',
            color: palette.neutralGrey[1000],
            '&:hover': {
              color: palette.neutralGrey[1300],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.blue[400]}`,
              color: palette.neutralGrey[1300],
            },
            '&:disabled': {
              color: palette.neutralGrey[500],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[500],
              },
            },
          },
        },
        {
          props: { variant: 'contained', color: 'success' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.green[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.green[1000],
              color: palette.neutralGrey[100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.green[200]}`,
              backgroundColor: palette.green[1000],
            },
            '&:disabled': {
              backgroundColor: palette.green[200],
              color: palette.neutralGrey[100],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[100],
              },
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[1000],
            },
            color: palette.neutralGrey[1000],
            border: `1.5px solid ${palette.green[800]}`,
            '&:hover': {
              border: `1.5px solid ${palette.green[1000]}`,
            },
            '&:focus': {
              border: 'none',
              boxShadow: `0px 0px 0px 4px ${palette.green[400]}`,
            },
            '&:disabled': {
              border: `1.5px solid ${palette.green[400]}`,
              color: palette.neutralGrey[800],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[800],
              },
            },
          },
        },
        {
          props: { variant: 'text', color: 'success' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.green[900],
            },
            color: palette.green[900],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.green[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.green[400]}`,
            },
            '&:disabled': {
              color: palette.green[500],
              '& .MuiSvgIcon-root': {
                color: palette.green[500],
              },
            },
          },
        },
        {
          props: { variant: 'contained', color: 'warning' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.orange[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.orange[1000],
            },
            '&:focus': {
              backgroundColor: palette.orange[1000],
              boxShadow: `0px 0px 0px 4px ${palette.orange[200]}`,
            },
            '&:disabled': {
              backgroundColor: palette.orange[300],
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.orange[1000],
            },
            color: palette.orange[1000],
            border: `1.5px solid ${palette.orange[800]}`,
            '&:hover': {
              border: `1.5px solid ${palette.orange[1000]}`,
              color: palette.orange[1100],
              backgroundColor: palette.neutralGrey[100],
            },
            '&:focus': {
              border: 'none',
              color: palette.orange[1200],
              boxShadow: `0px 0px 0px 4px ${palette.orange[200]}`,
            },
            '&:disabled': {
              color: palette.orange[300],
              border: `1.5px solid ${palette.orange[200]}`,
            },
          },
        },
        {
          props: { variant: 'text', color: 'warning' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.orange[1000],
            },
            color: palette.orange[1000],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.orange[1100],
            },
            '&:focus': {
              color: palette.orange[1200],
              boxShadow: `0px 0px 0px 4px ${palette.orange[200]}`,
            },
            '&:disabled': {
              color: palette.orange[300],
            },
          },
        },
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.neutralGrey[100],
            },
            backgroundColor: palette.red[800],
            color: palette.neutralGrey[100],
            '&:hover': {
              backgroundColor: palette.red[1000],
              color: palette.neutralGrey[100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.red[200]}`,
              backgroundColor: palette.red[1000],
            },
            '&:disabled': {
              backgroundColor: palette.red[300],
              color: palette.neutralGrey[100],
              '& .MuiSvgIcon-root': {
                color: palette.neutralGrey[100],
              },
            },
          },
        },
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.red[1000],
            },
            border: `1.5px solid ${palette.red[800]}`,
            color: palette.red[1000],
            '&:hover': {
              border: `1.5px solid ${palette.red[1000]}`,
              color: palette.red[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.red[200]}`,
              color: palette.red[1100],
            },
            '&:disabled': {
              border: `1.5px solid ${palette.red[200]}`,
              color: palette.red[300],
              '& .MuiSvgIcon-root': {
                color: palette.red[300],
              },
            },
          },
        },
        {
          props: { variant: 'text', color: 'error' },
          style: {
            '& .MuiSvgIcon-root': {
              color: palette.red[1000],
            },
            color: palette.red[1000],
            backgroundColor: 'transparent',
            '&:hover': {
              color: palette.red[1100],
            },
            '&:focus': {
              boxShadow: `0px 0px 0px 4px ${palette.red[200]}`,
              color: palette.red[1000],
            },
            '&:disabled': {
              color: palette.red[300],
              '& .MuiSvgIcon-root': {
                color: palette.red[300],
              },
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: palette.neutralGrey[300],
            boxShadow: 'none',
          },
          color: palette.primary.contrastText,
          borderRadius: 4,
          padding: 0,
        },
        sizeLarge: {
          '& .MuiSvgIcon-root': {
            height: 24,
            width: 24,
          },
          '& .OSUI-material-symbols': {
            fontSize: '24px !important',
          },
          height: 48,
          width: 48,
        },
        sizeMedium: {
          '& .MuiSvgIcon-root': {
            height: 24,
            width: 24,
          },
          '& .OSUI-material-symbols': {
            fontSize: '24px !important',
          },
          height: 40,
          width: 40,
        },
        sizeSmall: {
          '& .MuiSvgIcon-root': {
            height: 20,
            width: 20,
          },
          '& .OSUI-material-symbols': {
            fontSize: '20px !important',
          },
          height: 36,
          width: 36,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
          borderBottom: '1px solid ' + palette.grey[130],
          fontSize: 18,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',

          '&>span': {
            flexGrow: 1,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
          borderTop: '1px solid ' + palette.grey[130],
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        type: 'tab',
      },
      variants: [
        {
          props: { type: 'segmented' },
          style: {
            backgroundColor: palette.neutralGrey[200],
            '& .OSUI-Tabs-indicator': {
              display: 'none !important',
            },
            '& .OSUI-Tab-root': {
              '&.Mui-selected': {
                backgroundColor: palette.neutralGrey[100],
                boxShadow: '0px 1.5px 4px -1px rgba(10, 9, 11, 0.07)',
              },
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: 8,
          borderRadius: 8,
          minHeight: 'auto',
          height: 36,
          [createTheme().breakpoints.between(393, 'md')]: {
            padding: 6,
            height: 28,
          },
          [createTheme().breakpoints.down(393)]: {
            padding: 4,
            height: 28,
          },
        },
        flexContainer: {
          gap: 8,
          [createTheme().breakpoints.between(393, 'md')]: {
            gap: 6,
          },
          [createTheme().breakpoints.down(393)]: {
            gap: 4,
          },
        },
        indicator: {
          backgroundColor: palette.blue[900],
        },
        scroller: {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiTab: {
      defaultProps: {
        iconPosition: 'start',
      },
      styleOverrides: {
        root: {
          // This is using MUI Button
          borderRadius: 6,
          minHeight: 'auto',
          height: 36,
          gap: 6,
          color: palette.neutralGrey[900],
          ...typography.body2Medium,
          [createTheme().breakpoints.between(393, 'md')]: {
            ...typography.body3Medium,
            height: 28,
          },
          [createTheme().breakpoints.down(393)]: {
            ...typography.body4Medium,
            height: 28,
          },
          textTransform: 'none',
          '&.Mui-selected': {
            color: palette.blue[900],
          },
          '&.Mui-disabled': {
            color: palette.neutralGrey[500],
          },
          '& .OSUI-material-symbols.OSUI-Tab-iconWrapper': {
            fontSize: '16px !important',
          },
          '& .OSUI-Tab-iconWrapper': {
            margin: 0,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: palette.grey[120],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
        head: {
          fontSize: 13,
          padding: '10px 16px',
          color: palette.grey[810],
          borderBottom: 'none',
          '&:first-child': {
            borderTopLeftRadius: 8,
          },
          '&:last-child': {
            borderTopRightRadius: 8,
          },
        },
        body: {
          border: '1px solid ' + palette.grey[110],
          borderLeft: 'none',
          borderRight: 'none',
        },
      },
    },
    MuiToggleButtonGroup: {
      variants: [
        {
          props: { fullWidth: true },
          style: {
            width: '100%',
          },
        },
      ],
      styleOverrides: {
        root: {
          display: 'flex',
          padding: 4,
          backgroundColor: palette.secondary.main,
          boxSizing: 'border-box',
          gap: 4,
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          padding: '8px 0px',
          borderRadius: 8,
          boxShadow: '0px 2px 16px 0px rgba(17, 17, 28, 0.08)',
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          width: 328,
          height: 360,
          maxHeight: 360,
          boxSizing: 'border-box',
          padding: 12,
        },
      },
    },
    MuiPickersSlideTransition: {
      styleOverrides: {
        root: {
          minHeight: '265px !important',
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        root: {
          '& .OSUI-ButtonBase-root': {
            '&.Mui-selected': {
              border: 'none',
              backgroundColor: palette.blue[800],
              color: palette.neutralGrey[100],
              ...typography.body2,
              borderRadius: 4,
              '&:hover': {
                backgroundColor: palette.blue[800],
              },
              '&:focus': {
                backgroundColor: palette.blue[800],
              },
            },
            '&:not(.Mui-selected)': {
              border: 'none',
            },
            ...typography.body2,
            color: palette.neutralGrey[1000],
            fontStyle: 'normal',
            width: 40,
            height: 40,
            margin: 0,
          },
        },
        header: {
          justifyContent: 'space-between',
          paddingBottom: 8,
        },
        monthContainer: {
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        },
        weekContainer: {
          margin: 0,
          justifyContent: 'space-between',
        },
        weekDayLabel: {
          // There is no way to pass component to the dayOfWeek slot, so we need to override the styles
          textIndent: '-9999px',
          overflow: 'hidden',
          position: 'relative',
          width: 40,
          height: 'auto',
          margin: 0,
          '&::before': {
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            textIndent: '0',
            fontStyle: 'normal',
            ...typography.body3SemiBold,
            color: palette.neutralGrey[800],
          },
          ...Object.fromEntries(
            ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day, index) => [
              `&:nth-of-type(${index + 1})::before`,
              { content: `"${day}"` },
            ])
          ),
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
          '&.MuiPickersDay-dayOutsideMonth': {
            color: palette.neutralGrey[800],
          },
        },
        today: {
          border: `1px solid ${palette.neutralGrey[1300]} !important`,
          borderRadius: 4,
          '&:hover': {
            backgroundColor: palette.white,
          },
          '&:focus': {
            backgroundColor: palette.white,
          },
        },
      },
    },
    MuiPickersFadeTransitionGroup: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          padding: 0,
          width: 'auto',
          justifyContent: 'space-between',
          gap: 4,
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          flexBasis: 'auto',
        },
        yearButton: {
          ...typography.body2Medium,
          height: 40,
          width: 70,
          padding: '8px 4px',
          margin: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-selected': {
            color: palette.blue[1000],
            borderRadius: 4,
            backgroundColor: `${palette.blue[300]} !important`,
          },
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        root: {
          padding: 0,
          width: 'auto',
          justifyContent: 'space-between',
          gap: 4,
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          margin: 0,
          flexBasis: 'auto',
        },
        monthButton: {
          ...typography.body2Medium,
          width: 96,
          height: 40,
          padding: '8px 4px',
          margin: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-selected': {
            color: palette.blue[1000],
            borderRadius: 4,
            backgroundColor: `${palette.blue[300]} !important`,
          },
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          gap: 4,
          borderBottom: `1px solid ${palette.neutralGrey[700]}`,
          padding: 12,
          width: 'auto',
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        root: {
          width: '100%',
          '&:not(:first-of-type)': {
            border: 'none',
          },
          gap: 8,
          display: 'flex',
          flexDirection: 'column',
        },
        item: {
          ...typography.body2Medium,
          height: 40,
          width: 100,
          padding: '8px 4px',
          margin: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-selected': {
            color: palette.blue[1000],
            borderRadius: 4,
            backgroundColor: `${palette.blue[300]} !important`,
          },
        },
      },
    },
    MuiToggleButton: {
      variants: [
        {
          props: { color: 'primary' },
          style: {
            color: palette.primary.contrastText + ' !important',
          },
        },
        {
          props: { color: 'secondary' },
          style: {
            color: palette.grey[800] + ' !important',
          },
        },
        {
          props: { color: 'info' },
          style: {
            color: palette.info.contrastText + ' !important',
          },
        },
        {
          props: { color: 'success' },
          style: {
            color: palette.success.contrastText + ' !important',
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: palette.warning.contrastText + ' !important',
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: palette.error.contrastText + ' !important',
          },
        },
        {
          props: { selected: true },
          style: {
            backgroundColor: 'white !important',
            boxShadow: BOX_SHADOW_CONFIGS.soft,
            fontWeight: 'bold',
            letterSpacing: 0, // To mitigate spacing changes when selected (due to bolding)
          },
        },
        {
          props: { selected: true, color: 'primary' },
          style: {
            backgroundColor: palette.primary.main + ' !important',
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'none',
          background: 'none',
          border: 'none',
          borderRadius: '4px !important',
          flexGrow: 1,
          lineHeight: 1,
          fontWeight: 400,
          letterSpacing: '0.02rem', // To mitigate spacing changes when selected (due to bolding)
        },
        sizeSmall: {
          padding: '8px 12px',
          fontSize: 14,
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          zIndex: 2000, // To make sure the modal is on top of the bottom navigation (mobile)
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: 2,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          margin: 2,
          marginLeft: 0,
          '&.Mui-completed': {
            color: palette.blue[800],
          },
          '&.Mui-active': {
            color: palette.blue[800],
            border: `1px solid ${palette.blue[600]}`,
            padding: 1,
            borderRadius: '50%',
            margin: 0,
            marginLeft: -2,
          },
          '&.Mui-disabled': {
            opacity: 0.5,
            border: '3px solid red',
          },
        },
        text: {
          fill: 'white',
        },
      },
    },
    MuiStepButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: 0.5,
          },
        },
      },
    },
    MuiInputBase: {
      variants: [
        {
          props: { color: 'info' },
          style: {
            color: palette.info.contrastText,
            backgroundColor: palette.info.main,
            '& .OSUI-OutlinedInput-notchedOutline': {
              border: 'none',
              boxShadow: 'none',
            },
          },
        },
        {
          props: { color: 'warning' },
          style: {
            color: palette.warning.contrastText,
            backgroundColor: palette.warning.main,
            '& .OSUI-OutlinedInput-notchedOutline': {
              border: 'none',
              boxShadow: 'none',
            },
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: palette.error.contrastText,
            backgroundColor: palette.error.main,
            '& .OSUI-OutlinedInput-notchedOutline': {
              border: 'none',
              boxShadow: 'none',
            },
          },
        },
        {
          props: { color: 'success' },
          style: {
            color: palette.success.contrastText,
            backgroundColor: palette.success.main,
            '& .OSUI-OutlinedInput-notchedOutline': {
              border: 'none',
              boxShadow: 'none',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          padding: '0 12px !important',
          gap: 8,
        },
        inputSizeSmall: {
          '&.OSUI-Select-select': {
            padding: '4px 12px',
            fontSize: 14,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          // Band-aid: SoftWarning (and similar) can return null while still being wrapped in
          // InputAdornment, leaving an adornment that eats padding/gap. MUI5 also injects a
          // <span class="notranslate">\u200B</span> (ZWSP) for position="start" (Google Translate
          // workaround), so the root is never :empty in that case — hide when that span is
          // the only child. Note: this does not clear MUI's adornedStart/adornedEnd classes —
          // omit the adornment prop entirely when content is empty for a full fix.
          '&:empty, &:has(> .notranslate:only-child)': {
            display: 'none',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          position: 'unset',
          WebkitTransform: 'unset',
          color: palette.neutralGrey[1300],
          '&.Mui-focused': {
            color: palette.blue[800],
          },
          '&.Mui-error': {
            color: palette.red[800],
          },
          '&.Mui-disabled': {
            color: palette.neutralGrey[1300],
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: palette.white,
        },
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            backgroundColor: palette.neutralGrey[100],
            '& .OSUI-OutlinedInput-notchedOutline': {
              borderRadius: 4,
              boxShadow: 'none',
              border: `1px solid ${palette.neutralGrey[500]}`,
              '&:focus-visible': {
                border: `1.5px solid ${palette.blue[800]} !important`,
              },
              '&:hover': {
                border: 'none !important',
              },
            },
            '& .OSUI-OutlinedInput-input.Mui-disabled': {
              '-webkit-text-fill-color': palette.neutralGrey[1000],
            },
            '&.Mui-disabled': {
              // To change the color of the icon
              '-webkit-text-fill-color': palette.neutralGrey[800],
            },
            '&.Mui-disabled .OSUI-OutlinedInput-notchedOutline': {
              border: `1px solid ${palette.neutralGrey[500]}`,
              backgroundColor: 'transparent',
              color: `${palette.neutralGrey[900]} !important`,
              '& .OSUI-material-symbols': {
                color: `${palette.neutralGrey[800]} !important`,
              },
            },
            '& .OSUI-Select-select': {
              padding: 0,
              ...typography.body2,
              color: palette.neutralGrey[1300],
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              alignSelf: 'center',
              backgroundColor: 'transparent',
            },
            '&:hover': {
              border: 'none !important',
            },
          },
        },
        {
          props: { variant: 'outlined', error: true },
          style: {
            '& .OSUI-OutlinedInput-notchedOutline': {
              borderRadius: 4,
              boxShadow: 'none',
              border: `1.5px solid ${palette.red[800]} !important`,
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          padding: 0,
          minHeight: 44,
          gap: 8,
          margin: '0px !important',
        },
        icon: {
          color: palette.neutralGrey[900],
          '& .OSUI-material-symbols': {
            fontSize: '20px !important',
          },
        },
        select: {
          display: 'block',
          flexWrap: 'wrap',
          gap: 4,
          // paddingLeft: '16px !important',
          paddingRight: '40px !important',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          color: palette.grey[900],
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
          '& .OSUI-FormControlLabel-label': {
            ...typography.body2,
            color: palette.neutralGrey[1300],
          },
          '&.Mui-disabled .OSUI-FormControlLabel-label': {
            color: palette.neutralGrey[500],
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...typography.body3,
          '&.Mui-error': {
            color: `${palette.neutralGrey[900]} !important`,
          },
          '&.Mui-disabled': {
            color: `${palette.neutralGrey[900]} !important`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          gap: 4,
          '& .OSUI-FormHelperText-root': {
            padding: '2px 0px',
            color: palette.neutralGrey[900],
            fontStyle: 'normal',
            marginLeft: '0px !important',
            margin: '2px 0',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 44,
          ...typography.body2,
          fontSize: 14,
          // hover state
          '&:hover .OSUI-OutlinedInput-notchedOutline': {
            borderColor: palette.neutralGrey[900],
          },
          // focus state
          '&.Mui-focused .OSUI-OutlinedInput-notchedOutline': {
            borderColor: palette.blue[800],
          },
          // error state
          '&.Mui-error .OSUI-OutlinedInput-notchedOutline': {
            borderColor: palette.red[800],
            borderWidth: '1.5px',
          },
          // disabled state
          '&.Mui-disabled': {
            backgroundColor: palette.neutralGrey[200],
            color: palette.neutralGrey[900],
          },
          '&.Mui-disabled .OSUI-OutlinedInput-notchedOutline': {
            borderColor: palette.neutralGrey[500],
            color: palette.neutralGrey[900],
            '&::value': {
              color: palette.neutralGrey[1200],
              '-webkit-text-fill-color': palette.neutralGrey[1200] + ' !important',
            },
          },
          '& .OSUI-Select-select': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            alignSelf: 'center',
          },
        },
        input: {
          padding: '8.5px 0',
          '&::placeholder': {
            color: palette.neutralGrey[1200],
            '-webkit-text-fill-color': palette.neutralGrey[1200] + ' !important',
            ...typography.body2,
          },
        },
        notchedOutline: {
          borderRadius: 4,
          border: `1px solid ${palette.neutralGrey[500]}`,
          top: 0,
          '& legend': {
            display: 'none',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:after': {
            borderBottomColor: `${palette.neutralGrey[1200]}`,
          },
          '&.Mui-error:before': {
            borderBottomColor: `${palette.red[600]}`,
          },
          '&.Mui-error:after': {
            borderBottomColor: `${palette.red[700]}`,
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: 2000,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: BOX_SHADOW_CONFIGS.menu,
          marginTop: 4,
        },
        list: {
          border: `1px solid ${palette.secondary.light}`,
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            borderRadius: 4,
            margin: 0,
            padding: 8,
            // Use data attributes for custom styling
            '&[data-color="destructive"]': {
              color: palette.red[800],
            },
            '&[data-color="primary"]': {
              color: palette.neutralGrey[1300],
              '&:hover': {
                backgroundColor: palette.neutralGrey[200],
              },
            },
            // Selected state styling
            '&.Mui-selected': {
              backgroundColor: `${palette.blue[100]} !important`,
              color: palette.neutralGrey[1300],
              '&[data-color="destructive"]': {
                backgroundColor: `${palette.red[100]} !important`,
                color: palette.red[800],
              },
              '&:hover': {
                backgroundColor: palette.blue[200],
                '&[data-color="destructive"]': {
                  backgroundColor: palette.red[200],
                },
              },
            },
            // Disabled state styling
            '&.Mui-disabled': {
              color: palette.neutralGrey[600],
              '&[data-color="destructive"]': {
                color: `${palette.red[300]} !important`,
              },
              pointerEvents: 'none',
            },

            '&.OSUI-MenuItem-root': {
              alignItems: 'center',
            },

            '&.OSUI-MenuItem-divider': {
              border: 'none',
              marginBottom: 17,
              position: 'relative',
              '&::after': {
                backgroundColor: palette.neutralGrey[600],
                bottom: -17,
                content: "''",
                height: 1,
                left: -8,
                margin: '8px 0px',
                pointerEvents: 'none',
                position: 'absolute',
                width: 'calc(100% + 16px)',
              },
            },
            '& .OSUI-Checkbox-root': {
              padding: 0,
              marginRight: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
            // Checkbox colors based on MenuItem color
            '&[data-color="primary"] .OSUI-Checkbox-root': {
              '&.Mui-checked': {
                color: palette.blue[800],
              },
            },
            '&[data-color="destructive"] .OSUI-Checkbox-root': {
              '&.Mui-checked': {
                color: palette.red[800],
              },
            },
            // Section heading styling - no hover, not clickable
            '&[data-content-type="sectionHeading"]': {
              cursor: 'default',
              pointerEvents: 'none',
              '&:hover': {
                backgroundColor: 'transparent !important',
              },
              '&:focus': {
                backgroundColor: 'transparent !important',
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent !important',
                boxShadow: 'none !important',
              },
            },
            // General icon sizing for all states
            '& .MuiSvgIcon-root': {
              height: 20,
              width: 20,
            },
          }
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .Mui-error': {
            color: palette.error.contrastText + ' !important',
          },
          '& .OSUI-OutlinedInput-input.Mui-disabled': {
            '-webkit-text-fill-color': palette.neutralGrey[1000],
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          overflow: 'visible',
          alignSelf: 'flex-end',
        },
        selectLabel: {
          color: palette.neutralGrey[1000],
          ...typography.body1Medium,
          [createTheme().breakpoints.between(393, 'lg')]: {
            ...typography.body2Medium,
          },
          [createTheme().breakpoints.down(393)]: {
            ...typography.body3Medium,
          },
        },
        actions: {
          display: 'flex',
          gap: 8,
          margin: '0px !important',
        },
        toolbar: {
          padding: '0px !important',
          gap: 14,
          [createTheme().breakpoints.between(393, 'lg')]: {
            gap: 12,
          },
          [createTheme().breakpoints.down(393)]: {
            gap: 8,
          },
        },
        select: {
          margin: '0px !important',
        },
        input: {
          margin: '0px !important',
        },
        spacer: {
          display: 'none', // To avoid adding gap after rows per page
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        tooltip: ({ ownerState }) => {
          const backgroundColor =
            ownerState.color === 'primary'
              ? palette.grey[1200]
              : ownerState.color === 'secondary'
                ? palette.white
                : palette.grey[1200]

          const textColor =
            ownerState.color === 'primary'
              ? `${palette.white} !important`
              : ownerState.color === 'secondary'
                ? `${palette.grey[1200]} !important`
                : `${palette.white} !important`

          return {
            padding: '8px !important',
            boxShadow: '0px 2px 16px 0px rgba(17, 17, 28, 0.08)',
            margin: '0 !important',
            backgroundColor,
            color: textColor,
            '& a': {
              color: textColor,
            },
          }
        },
        arrow: ({ ownerState }) => ({
          color:
            ownerState.color === 'primary'
              ? palette.grey[1200]
              : ownerState.color === 'secondary'
                ? palette.white
                : palette.grey[1200],
        }),
        popper: () => ({
          zIndex: 3000,
        }),
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: 'displayLarge' },
          style: {
            ...typography.displayLarge,
          },
        },
        {
          props: { variant: 'displayMedium' },
          style: {
            ...typography.displayMedium,
          },
        },
        {
          props: { variant: 'displaySmall' },
          style: {
            ...typography.displaySmall,
          },
        },
        {
          props: { variant: 'h1' },
          style: {
            ...typography.h1,
          },
        },
        {
          props: { variant: 'h2' },
          style: {
            ...typography.h2,
          },
        },
        {
          props: { variant: 'h3' },
          style: {
            ...typography.h3,
          },
        },
        {
          props: { variant: 'h4' },
          style: {
            ...typography.h4,
          },
        },
        {
          props: { variant: 'h5' },
          style: {
            ...typography.h5,
          },
        },
        {
          props: { variant: 'h6' },
          style: {
            ...typography.h6,
          },
        },
        {
          props: { variant: 'body1' },
          style: {
            ...typography.body1,
          },
        },
        {
          props: { variant: 'body1Medium' },
          style: {
            ...typography.body1Medium,
          },
        },
        {
          props: { variant: 'body1SemiBold' },
          style: {
            ...typography.body1SemiBold,
          },
        },
        {
          props: { variant: 'body2' },
          style: {
            ...typography.body2,
          },
        },
        {
          props: { variant: 'body2Medium' },
          style: {
            ...typography.body2Medium,
          },
        },
        {
          props: { variant: 'body2SemiBold' },
          style: {
            ...typography.body2SemiBold,
          },
        },
        {
          props: { variant: 'body3' },
          style: {
            ...typography.body3,
          },
        },
        {
          props: { variant: 'body3Medium' },
          style: {
            ...typography.body3Medium,
          },
        },
        {
          props: { variant: 'body3SemiBold' },
          style: {
            ...typography.body3SemiBold,
          },
        },
        {
          props: { variant: 'body4' },
          style: {
            ...typography.body4,
          },
        },
        {
          props: { variant: 'body4Medium' },
          style: {
            ...typography.body4Medium,
          },
        },
        {
          props: { variant: 'body4SemiBold' },
          style: {
            ...typography.body4SemiBold,
          },
        },
        {
          props: { variant: 'caption' },
          style: {
            ...typography.caption,
          },
        },
        {
          props: { variant: 'overline' },
          style: {
            ...typography.overline,
          },
        },
      ],
      styleOverrides: {
        root: {
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          width: '100%',
          marginLeft: 12,
          marginRight: 12,
          color: palette.neutralGrey[100],

          // default styles
          '> .OSUI-Slider-rail': {
            backgroundColor: palette.neutralGrey[400],
          },
          '> .OSUI-Slider-thumb': {
            color: palette.neutralGrey[100],
            '&:hover': {
              boxShadow: 'none',
            },
          },
          ' .Mui-focusVisible': {
            boxShadow: 'none !important', // override default MUI behavior
          },
          '&.Mui-disabled': {
            '> .OSUI-Slider-track': {
              opacity: 0.5,
            },
            '.OSUI-Slider-markLabel': {
              color: palette.neutralGrey[600],
            },
          },
          // styles specific to color types
          '&.OSUI-Slider-colorPrimary': {
            '> .OSUI-Slider-track': {
              backgroundColor: palette.blue[800],
              border: 'none',
            },
          },
          '&.OSUI-Slider-colorError': {
            '> .OSUI-Slider-track': {
              backgroundColor: palette.red[800],
              border: 'none',
            },
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: ({ ownerState }) =>
          ownerState.color === 'primary'
            ? {
                backgroundColor: 'rgba(83, 167, 252, 0.08)',
                '&::after': {
                  background: `linear-gradient(
                          90deg,
                          transparent 0%,
                          rgba(53, 124, 230, 0.25),
                          rgba(83, 167, 252, 0.25),
                          rgba(83, 167, 252, 0.04),
                          transparent 100%
                        )`,
                  opacity: 0.75,
                },
              }
            : {
                backgroundColor: 'rgba(200, 200, 200, 0.12)',
                '&::after': {
                  background: `linear-gradient(
                          90deg,
                          transparent 0%,
                          rgba(230, 230, 230, 0.25),
                          rgba(252, 252, 252, 0.25),
                          rgba(252, 252, 252, 0.04),
                          transparent 100%
                        )`,
                },
              },
      },
    },
  },
})

// Type augmentation for custom mixins
declare module '@mui/material/styles/createMixins' {
  interface Mixins {
    borderStandard: CSSProperties
    outlineStandard: CSSProperties
    fileInputDropzone: CSSProperties
  }
}

export type BaseThemeType = typeof BaseTheme
export { palette }
